import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, AlertTriangle, Film, RotateCcw } from 'lucide-react'

/**
 * Instant Replay — plays a broadcast from a given moment.
 *
 * The job is "show me this detection", not "give me a video player". A
 * reviewer has clicked a brand and wants the few seconds around the frame the
 * model read; the browser's own controls cover everything after that, so this
 * adds only what the task needs and nothing it does not.
 *
 * WHY SEEKING IS CHEAP HERE. The file is a fragmented MP4 with a global index
 * at the front — about 135 KB, against the 13.4 MB index an ordinary MP4 keeps
 * at the END and makes the player read in full before it can show anything.
 * Measured on a 3h07m game: one frame costs 5-6 MB of ranged reads instead of
 * 17-19 MB. Keyframes land every 2.00s, so a seek is accurate to within two
 * seconds without any extra work.
 *
 * State the saving in BYTES, not seconds. The fixed ~12.8 MB index is paid on
 * every cold load, and what that is worth depends entirely on the viewer's
 * link — about 20s at 5 Mbps, about 2s at 40. The benefit is largest for
 * whoever has the worst connection, which is the case worth designing for,
 * but "25 seconds becomes instant" was a measurement of a degraded link and
 * not a property of the file.
 */

export interface PlaybackTarget {
  url: string
  expiresAt: string
  durationMs: number | null
  bytes: number | null
}

interface Props {
  /** Resolves a fresh signed URL. Called again when the current one expires. */
  fetchUrl: () => Promise<PlaybackTarget>
  /** Where to start, in milliseconds from the top of the broadcast. */
  startMs?: number
  /** Shown while the server is still remuxing (HTTP 409). */
  preparingHint?: string
  className?: string
}

type State =
  | { kind: 'loading' }
  | { kind: 'preparing' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; target: PlaybackTarget }

const fmt = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}

const BroadcastPlayer: React.FC<Props> = ({
  fetchUrl, startMs = 0, preparingHint, className = ''
}) => {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // Survives the reload a refreshed URL causes, so an expiry mid-watch does
  // not throw the viewer back to the start of a three-hour game.
  const resumeAtRef = useRef<number>(startMs / 1000)

  const load = useCallback(async () => {
    setState({ kind: 'loading' })
    try {
      const target = await fetchUrl()
      setState({ kind: 'ready', target })
    } catch (err: any) {
      // 409 is "still being prepared" — a state, not a failure. The copy is
      // built asynchronously after upload, so this is the expected answer for
      // a broadcast someone opens straight after uploading it.
      const status = err?.status ?? err?.response?.status
      if (status === 409) setState({ kind: 'preparing' })
      else setState({ kind: 'error', message: err?.message ?? 'Could not load the video' })
    }
  }, [fetchUrl])

  useEffect(() => { void load() }, [load])

  // A new startMs means the reviewer clicked a different appearance. Seek
  // rather than reload: the index is already in the player.
  useEffect(() => {
    resumeAtRef.current = startMs / 1000
    const v = videoRef.current
    if (v && state.kind === 'ready' && v.readyState >= 1) {
      v.currentTime = startMs / 1000
      void v.play().catch(() => {/* autoplay refusal is not an error */})
    }
  }, [startMs, state.kind])

  const onLoadedMetadata = () => {
    const v = videoRef.current
    if (!v) return
    v.currentTime = resumeAtRef.current
  }

  /**
   * A signed URL that expires mid-playback surfaces as a decode/network error,
   * which looks exactly like a corrupt file. Fetch a fresh one and resume
   * where the viewer was, rather than showing them a broken video.
   */
  const onError = () => {
    const v = videoRef.current
    if (v && v.currentTime > 0) resumeAtRef.current = v.currentTime
    void load()
  }

  if (state.kind === 'loading') {
    return (
      <div className={`flex items-center justify-center bg-gray-900 rounded-lg aspect-video ${className}`}>
        <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (state.kind === 'preparing') {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-gray-900 rounded-lg aspect-video px-6 text-center ${className}`}>
        <Film className="h-6 w-6 text-gray-500" />
        <p className="text-xs font-medium text-gray-300">Instant Replay is not ready</p>
        {/* DOES NOT CLAIM A BUILD IS RUNNING. A 409 means only that the
            seekable copy is absent, and the two reasons look identical from
            here: a remux still in flight, or a broadcast uploaded before the
            remux existed, where nothing is happening and nothing will. The
            old copy promised "a few minutes" to both, so the second kind
            waited forever for something nobody had started. */}
        <p className="text-[11px] text-gray-500 max-w-xs">
          {preparingHint ?? 'A seekable copy has to be built before a broadcast can be scrubbed. If one was uploaded recently this takes a few minutes; otherwise ask support to build it.'}
        </p>
        <button
          onClick={() => void load()}
          className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-gray-300 bg-white/10 rounded hover:bg-white/20 transition-colors"
        >
          <RotateCcw className="h-3 w-3" /> Check again
        </button>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-gray-900 rounded-lg aspect-video px-6 text-center ${className}`}>
        <AlertTriangle className="h-6 w-6 text-amber-500" />
        <p className="text-xs font-medium text-gray-300">{state.message}</p>
        <button
          onClick={() => void load()}
          className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-gray-300 bg-white/10 rounded hover:bg-white/20 transition-colors"
        >
          <RotateCcw className="h-3 w-3" /> Try again
        </button>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      <video
        ref={videoRef}
        src={state.target.url}
        controls
        // metadata only: the whole point of the remux is that the header is
        // tiny and the player fetches ranges as it needs them. `auto` would
        // start pulling a 4 GB file nobody asked for.
        preload="metadata"
        playsInline
        onLoadedMetadata={onLoadedMetadata}
        onError={onError}
        className="w-full rounded-lg bg-black aspect-video"
      />
      {startMs > 0 && (
        <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-mono text-white tabular-nums">
          from {fmt(startMs)}
        </span>
      )}
    </div>
  )
}

export default BroadcastPlayer
